import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Close } from "@mui/icons-material";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../@shared/firebase/config";
import { useRoomsStore } from "../@shared/store/useRoomsStore";
import NeoButton from "./NeoButton";

const styles = {
  drawer: {
    position: "fixed",
    left: 0,
    top: 0,
    bottom: 0,
    width: "80vw",
    maxWidth: "320px",
    backgroundColor: "#16116e",
    paddingTop: 60,
    paddingLeft: 10,
    paddingRight: 10,
    zIndex: 9999,
    display: "flex",
    flexDirection: "column",
  },
  backdrop: {
    position: "fixed",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "rgba(0,0,0,0.4)",
    zIndex: 998,
  },
  drawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 30,
  },
  drawerTitle: {
    fontSize: 22,
    fontWeight: "bold",
    color: "#fff",
  },
  messagesContainer: {
    flex: 1,
    overflowY: "auto",
    paddingBottom: 100,
  },
  messageBubble: {
    backgroundColor: "#28228a",
    padding: 8,
    margin: "4px 0",
    borderRadius: 10,
    maxWidth: "90%",
    alignSelf: "flex-start",
    marginBottom: 8,
  },
  selfBubble: {
    backgroundColor: "#553cc6",
    alignSelf: "flex-end",
  },
  sender: {
    fontWeight: "bold",
    color: "#fff",
    marginBottom: 4,
  },
  messageText: {
    color: "#eee",
  },
  inputBar: {
    marginTop: "auto",
    display: "flex",
    alignItems: "center",
    padding: 10,
    backgroundColor: "#222",
    borderRadius: 10,
  },
  input: {
    flex: 1,
    color: "#fff",
    padding: 8,
    backgroundColor: "#333",
    borderRadius: 8,
    marginRight: 8,
    border: "none",
    outline: "none",
  },
  sendText: {
    color: "#00f2ff",
    fontWeight: "bold",
    cursor: "pointer",
  },
};

const ChatDrawer = ({ userData, roomId, visible, onClose }) => {
  const { chatMessages } = useRoomsStore();
  const [newMessage, setNewMessage] = useState("");
  const messagesRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive and drawer is visible.
  useEffect(() => {
    if (visible && messagesRef.current) {
      setTimeout(() => {
        messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
      }, 100);
    }
  }, [chatMessages, visible]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    try {
      await addDoc(collection(db, "rooms", roomId, "chatThreadData"), {
        message: newMessage.trim(),
        sender: userData.username,
        senderId: userData.id,
        createdAt: serverTimestamp(),
      });
      setNewMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  return (
    <>
      {visible && <div style={styles.backdrop} onClick={onClose}></div>}

      <motion.div
        initial={{ x: "-100vw" }}
        animate={{ x: visible ? 0 : "-100vw" }}
        transition={{ duration: 0.4 }}
        style={styles.drawer}
      >
        {/* Header */}
        <div style={styles.drawerHeader}>
          <div style={styles.drawerTitle}>Chat</div>
          <div onClick={onClose} style={{ cursor: "pointer", color: "#fff" }}>
            <Close fontSize="small" />
          </div>
        </div>

        {/* Messages */}
        <div style={styles.messagesContainer} ref={messagesRef}>
          {chatMessages.map((item) => (
            <div
              key={item.id}
              style={{
                ...styles.messageBubble,
                ...(item.senderId === userData.id ? styles.selfBubble : {}),
              }}
            >
              <div style={styles.sender}>
                {item.sender === "system" ? "🌀 System" : item.sender}
              </div>
              <div style={styles.messageText}>{item.message}</div>
            </div>
          ))}
        </div>

        {/* Input */}
        <div style={styles.inputBar}>
          <input
            style={styles.input}
            placeholder="Type a message..."
            placeholderTextColor="#aaa"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSend();
              }
            }}
          />
          <div onClick={handleSend} style={styles.sendText}>
            Send
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default ChatDrawer;
